import { useCallback } from "react";
import { RefreshCcw } from "lucide-react";

import {
  RiskService,
  type DimensionSummary,
  type FrameworkAlignmentResponse,
  type RecomputeRequest,
  type RiskDashboardResponse,
  type RiskScoreSnapshotResponse,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { usePoll } from "@/hooks/use-poll";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatFramework, formatScore } from "@/lib/risk-helpers";
import { DIMENSION_KEY } from "@/components/risk/risk-dimension-keys";
import { formatRelativeTimeID } from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DeltaIndicator } from "@/components/shared/delta-indicator";
import { ScoreBandBadge } from "@/components/shared/score-band-badge";
import { Section } from "@/components/shared/section";
import { formatMaturityLevel } from "@/lib/compliance-helpers";

const POLL_INTERVAL_MS = 30_000;

export function RiskDashboardTab() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const dashboardFetcher = useCallback(
    () => RiskService.getDashboard(),
    []
  );
  const dashboard = useApi<RiskDashboardResponse>(dashboardFetcher);

  // Pull the latest snapshot too — it carries the deltas vs the
  // previous snapshot which the dashboard view doesn't surface
  // directly. Some tenants have no snapshots yet (fresh demo
  // accounts), so handle the 404 gracefully via the same useApi
  // error channel.
  const latestFetcher = useCallback(
    () => RiskService.getLatestSnapshot(),
    []
  );
  const latest = useApi<RiskScoreSnapshotResponse>(latestFetcher);

  usePoll(
    async () => {
      await Promise.allSettled([
        dashboard.refetch(),
        latest.refetch(),
      ]);
    },
    POLL_INTERVAL_MS,
    { enabled: Boolean(user) }
  );

  const recompute = useMutation<RecomputeRequest, RiskScoreSnapshotResponse>(
    (body) => RiskService.recompute(body),
    {
      onSuccess: () => {
        void dashboard.refetch();
        void latest.refetch();
      },
    }
  );

  const handleRecompute = () => {
    void recompute.mutate({});
  };

  return (
    <div className="space-y-6">
      {dashboard.error && (
        <Alert variant="warning">{parseApiError(dashboard.error)}</Alert>
      )}
      {recompute.error && (
        <Alert variant="danger">{parseApiError(recompute.error)}</Alert>
      )}

      <div className="flex flex-col gap-3 tablet:flex-row tablet:items-center tablet:justify-between">
        <p className="text-sm text-neutral-600">
          {t("risk.dashboard.intro")}
        </p>
        <Button
          onClick={handleRecompute}
          disabled={recompute.isLoading}
          variant="outline"
          size="sm"
        >
          <RefreshCcw size={14} />
          {recompute.isLoading
            ? t("risk.dashboard.recomputeLoading")
            : t("risk.dashboard.recompute")}
        </Button>
      </div>

      <OverallScoreCard
        dashboard={dashboard.data}
        latest={latest.data}
        isLoading={dashboard.isLoading && !dashboard.data}
      />

      <Section
        title={t("risk.dashboard.dimensionsTitle")}
        description={t("risk.dashboard.dimensionsDescription")}
      >
        <DimensionsTable
          dimensions={dashboard.data?.dimensions ?? []}
          isLoading={dashboard.isLoading && !dashboard.data}
        />
      </Section>

      <Section
        title={t("risk.dashboard.frameworksTitle")}
        description={t("risk.dashboard.frameworksDescription")}
      >
        <FrameworkAlignmentsTable
          rows={dashboard.data?.framework_alignments ?? []}
          isLoading={dashboard.isLoading && !dashboard.data}
        />
      </Section>
    </div>
  );
}

function OverallScoreCard({
  dashboard,
  latest,
  isLoading,
}: {
  dashboard: RiskDashboardResponse | null;
  latest: RiskScoreSnapshotResponse | null;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-ocean-100 bg-white p-5">
      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-3">
        <div>
          <div className="label-eyebrow">
            {t("risk.dashboard.overallScoreLabel")}
          </div>
          {isLoading ? (
            <Skeleton className="mt-1 h-8 w-24" />
          ) : (
            <div className="mt-1 text-3xl font-medium text-ocean-900">
              {formatScore(dashboard?.overall_score)}
            </div>
          )}
          {dashboard && <ScoreBandBadge band={dashboard.score_band} />}
        </div>

        <div>
          <div className="label-eyebrow">{t("risk.dashboard.deltaLabel")}</div>
          {isLoading ? (
            <Skeleton className="mt-1 h-7 w-16" />
          ) : (
            <div className="mt-1 text-lg">
              <DeltaIndicator delta={latest?.delta_overall ?? null} />
            </div>
          )}
        </div>

        <div>
          <div className="label-eyebrow">
            {t("risk.dashboard.maturityLabel")}
          </div>
          {isLoading ? (
            <Skeleton className="mt-1 h-7 w-20" />
          ) : (
            <div className="mt-1 text-sm text-ocean-900">
              {formatMaturityLevel(dashboard?.nist_maturity_level ?? null)}
            </div>
          )}
          {dashboard?.computed_at && (
            <div className="mt-1 text-xs text-neutral-500">
              {t("risk.dashboard.computedAt", {
                time: formatRelativeTimeID(dashboard.computed_at),
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DimensionsTable({
  dimensions,
  isLoading,
}: {
  dimensions: DimensionSummary[];
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("risk.dashboard.dimensionsColDimension")}</TableHead>
            <TableHead>{t("risk.dashboard.dimensionsColScore")}</TableHead>
            <TableHead>{t("risk.dashboard.dimensionsColDelta")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && dimensions.length === 0 && (
            <>
              {[0, 1, 2, 3].map((i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={3} className="py-4">
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            </>
          )}
          {!isLoading && dimensions.length === 0 && (
            <TableEmpty
              colSpan={3}
              message={t("risk.dashboard.dimensionsEmpty")}
            />
          )}
          {dimensions.map((dim) => (
            <TableRow key={dim.dimension}>
              <TableCell className="font-medium text-ocean-900">
                {DIMENSION_KEY[dim.dimension]
                  ? t(DIMENSION_KEY[dim.dimension])
                  : dim.dimension}
              </TableCell>
              <TableCell>{formatScore(dim.score)}</TableCell>
              <TableCell>
                <DeltaIndicator delta={dim.delta} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function FrameworkAlignmentsTable({
  rows,
  isLoading,
}: {
  rows: FrameworkAlignmentResponse[];
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("risk.dashboard.frameworksColFramework")}</TableHead>
            <TableHead>{t("risk.dashboard.frameworksColReadiness")}</TableHead>
            <TableHead>{t("risk.dashboard.frameworksColMaturity")}</TableHead>
            <TableHead className="hidden tablet:table-cell">
              {t("risk.dashboard.frameworksColComputed")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="py-4">
                <Skeleton className="h-5 w-full" />
              </TableCell>
            </TableRow>
          )}
          {!isLoading && rows.length === 0 && (
            <TableEmpty
              colSpan={4}
              message={t("risk.dashboard.frameworksEmpty")}
            />
          )}
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="font-medium text-ocean-900">
                {formatFramework(row.framework)}
              </TableCell>
              <TableCell>
                <Badge tone="ocean">{formatScore(row.readiness_score)}</Badge>
              </TableCell>
              <TableCell>{formatMaturityLevel(row.maturity_level)}</TableCell>
              <TableCell className="hidden text-neutral-600 tablet:table-cell">
                {formatRelativeTimeID(row.computed_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
